import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { getMeControllerObterQueryKey, useMeControllerAtualizar } from '@/api/generated/me/me';
import { AtualizarMeDtoUfCrmv, type MeResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { CamposDeEndereco } from '@/shared/components/CamposDeEndereco';
import { enderecoFormSchema, enderecoParaEnvio, enderecoParaFormulario } from '@/shared/lib/endereco';
import { bloquearEnvioPorEnter } from '@/shared/lib/formulario';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/** O `<Select>` do Radix não aceita `value=""` — daí a sentinela. */
const SEM_UF = 'nenhuma';

/**
 * As UFs vêm do contrato gerado, não de outra feature: `login.schema` tem a
 * mesma lista, mas importar de lá cruzaria a fronteira entre features. O `const`
 * do Orval é a própria fonte — se o backend mudar o enum, isto muda junto.
 */
const UFS = Object.values(AtualizarMeDtoUfCrmv);
type Uf = (typeof UFS)[number];

/**
 * Espelha os limites de `atualizarMeSchema` no backend.
 *
 * ⚠️ Só estes campos, nem um a mais: o schema do servidor é `.strict()`, então
 * chave extra no corpo vira 422 — inclusive `email` e `crmv`, que ficam de fora
 * de propósito (o DTO restrito existe justamente para barrar campo privilegiado).
 */
const schema = z.object({
  nomeCompleto: z.string().trim().min(3, 'Informe o nome completo').max(200),
  ufCrmv: z.string(),
  telefone: z.string().trim().max(20),
  especialidade: z.string().trim().max(120),
  clinica: z.string().trim().max(200),
  /** Texto de apresentação do especialista — o médico vê antes de escolher. */
  bio: z.string().trim().max(1000, 'Máximo de 1000 caracteres.'),
  /** Endereço da clínica — exigido para assinar receituário de controle especial. */
  endereco: enderecoFormSchema,
});

type Formulario = z.infer<typeof schema>;

/** O `ufCrmv` do servidor é `string | null`; o select só aceita UF conhecida. */
function ufDoServidor(valor: string | null | undefined): string {
  return valor && (UFS as readonly string[]).includes(valor) ? valor : SEM_UF;
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  me: MeResponse;
}

export function PerfilFormDialogo({ aberto, aoFechar, me }: Props) {
  const queryClient = useQueryClient();
  const atualizar = useMeControllerAtualizar();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;

    reset({
      nomeCompleto: me.perfil.nomeCompleto,
      ufCrmv: ufDoServidor(me.perfil.ufCrmv),
      telefone: me.perfil.telefone ?? '',
      especialidade: me.perfil.especialidade ?? '',
      clinica: me.perfil.clinica ?? '',
      bio: me.perfil.bio ?? '',
      endereco: enderecoParaFormulario(me.perfil.endereco),
    });
  }, [aberto, me, reset]);

  const bioDigitada = watch('bio') ?? '';

  const aoEnviar = handleSubmit(async (dados) => {
    // Campo esvaziado significa "apagar", não "não mexer": o formulário mostra
    // o valor atual, então o que a pessoa vê é o que ela envia. Os cinco são
    // `.nullable()` no servidor.
    const opcional = (v: string) => (v.trim() ? v.trim() : null);

    try {
      const atualizado = await atualizar.mutateAsync({
        data: {
          nomeCompleto: dados.nomeCompleto,
          telefone: opcional(dados.telefone),
          especialidade: opcional(dados.especialidade),
          clinica: opcional(dados.clinica),
          ufCrmv: dados.ufCrmv === SEM_UF ? null : (dados.ufCrmv as Uf),
          bio: opcional(dados.bio),
          endereco: enderecoParaEnvio(dados.endereco),
        },
      });

      // O PATCH devolve o `MeResponse` inteiro, então o cache recebe a resposta
      // do servidor em vez de um GET extra — e a tela passa a mostrar o que foi
      // gravado, não o que foi digitado. É a MESMA query do `AuthProvider`, que
      // alimenta o header e a sidebar: o nome muda nos três de uma vez.
      queryClient.setQueryData(getMeControllerObterQueryKey(), atualizado);
      toast.success('Perfil atualizado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o perfil.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar perfil</DialogTitle>
          <DialogDescription>
            E-mail e número do CRMV não mudam por aqui — fale com um administrador.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} onKeyDown={bloquearEnvioPorEnter} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="nomeCompleto"
            rotulo="Nome completo"
            obrigatorio
            erro={errors.nomeCompleto?.message}
          >
            <Input id="nomeCompleto" className={CLASSE_CONTROLE} {...register('nomeCompleto')} />
          </Campo>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Campo
              id="crmv"
              rotulo="CRMV"
              dica="O número é alterado por um administrador; aqui só muda a UF."
            >
              <Input id="crmv" disabled value={me.perfil.crmv ?? ''} className={CLASSE_CONTROLE} />
            </Campo>

            <Campo id="ufCrmv" rotulo="UF do CRMV" erro={errors.ufCrmv?.message}>
              <Controller
                name="ufCrmv"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="ufCrmv" className={CLASSE_CONTROLE} aria-label="UF do CRMV">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SEM_UF}>Nenhuma</SelectItem>
                      {UFS.map((uf) => (
                        <SelectItem key={uf} value={uf}>
                          {uf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>
          </div>

          <Campo id="telefone" rotulo="Telefone" erro={errors.telefone?.message}>
            <Input id="telefone" className={CLASSE_CONTROLE} {...register('telefone')} />
          </Campo>

          <Campo id="especialidade" rotulo="Especialidade" erro={errors.especialidade?.message}>
            <Input id="especialidade" className={CLASSE_CONTROLE} {...register('especialidade')} />
          </Campo>

          <Campo id="clinica" rotulo="Clínica" erro={errors.clinica?.message}>
            <Input id="clinica" className={CLASSE_CONTROLE} {...register('clinica')} />
          </Campo>

          <Campo
            id="bio"
            rotulo="Descrição"
            erro={errors.bio?.message}
            dica="Texto de apresentação como especialista — o médico vê antes de escolher."
          >
            <Textarea id="bio" rows={4} className={CLASSE_CONTROLE} {...register('bio')} />
          </Campo>
          <p className="text-right text-xs text-muted-foreground -mt-3">{bioDigitada.length}/1000</p>

          <fieldset className="space-y-3 border-t border-border pt-4">
            <legend className="text-sm font-semibold text-foreground">Endereço da clínica</legend>
            <p className="text-xs text-muted-foreground">
              Obrigatório para assinar receituário de controle especial: logradouro, número, cidade e UF.
            </p>
            <CamposDeEndereco control={control} setValue={setValue} erros={errors.endereco} />
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
